import { findChatById } from '@/lib/chatIndex';
import React, { useMemo, useCallback, useState } from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import { useAuthStore, useChatInputStore, useChatStore, useModalStore } from '@/store';
import PropTypes from 'prop-types';
import { PLUS_COLORS } from '@/constants/Colors';
import { PLUS_MONTHLY_CREDITS_DISPLAY, RESPONSE_ACTION_TYPES } from '@/constants/config';
import { isModelAccessMessageError } from '@/constants/utils/response_errors';
import { openModelAccessPaywall } from '@/constants/utils/trial';
import { startWorkFromPrompt } from '@/lib/startWorkFromPrompt';

const createStyles = (colors) => StyleSheet.create({
  container: {
    marginBottom: 8,
    maxWidth: '100%',
  },
  card: {
    backgroundColor: colors.solid_color_secondary,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: PLUS_COLORS.borderColorPurple,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
    gap: 12,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    ...(Platform.OS === 'web' ? { backgroundImage: PLUS_COLORS.backgroundImage } : { backgroundColor: PLUS_COLORS.textColorPurple }),
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerText: {
    flex: 1,
  },
  title: {
    marginBottom: 2,
  },
  originalMessageContainer: {
    backgroundColor: colors.solid_color_tertiary,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
  },
  originalMessageLabel: {
    marginBottom: 6,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  originalMessageText: {
    lineHeight: 22,
  },
  footer: {
    paddingHorizontal: 16,
    paddingVertical: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
  },
  benefitsList: {
    flex: 1,
    gap: 4,
  },
  benefitItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
});

function PlusRequiredMessage({ message }) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { openModal } = useModalStore(state => state.actions);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus === true);
  const router = useRouter();
  const [isStartingWork, setIsStartingWork] = useState(false);
  const contextId = useChatInputStore(state => state.currentContextId);
  const collectionId = useChatStore(state => findChatById(state.chats, contextId)?.inCollectionId);
  const agentId = useChatStore(state => findChatById(state.chats, contextId)?.agentId);

  // Get the original message content (strip any HTML tags if present)
  const originalMessage = useMemo(() => {
    if (!message?.content) return '';
    return message.content.replace(/<[^>]*>?/gm, '').trim();
  }, [message?.content]);
  const messageFiles = message?.files;
  const workFileIds = useMemo(() => (messageFiles || [])
    .map((file) => file?.id || file?._id || file)
    .filter(Boolean)
    .map(String), [messageFiles]);

  // Prefer buy-credits action copy for model gates; else go-plus.
  const buttonData = message?.buttons?.find(btn => btn?.action === RESPONSE_ACTION_TYPES.BUY_CREDITS)?.data
    || message?.buttons?.find(btn => btn?.action === RESPONSE_ACTION_TYPES.GO_PLUS)?.data;
  const isModelGate = isModelAccessMessageError(message);
  const isWorkModeGate = buttonData?.feature === 'work_mode'
    || message?.errorType === 'WORK_MODE_PLUS_REQUIRED'
    || message?.parameters?.___workModePlusRequired === true;
  const title = (isWorkModeGate && isPlus)
    ? 'Your task is ready for Work mode'
    : buttonData?.title || (isWorkModeGate
    ? 'This task is built for Work mode'
    : (isModelGate ? 'Unlock Premium AI Models' : 'Unlock more with Plus'));
  const subtitle = (isWorkModeGate && isPlus)
    ? 'Your original prompt is preserved. Run it now with an AI agent that can plan, research, use tools and files, and deliver finished work.'
    : buttonData?.subtitle || (isModelGate
    ? 'To use this model, buy credits or upgrade to Plus.'
    : (isWorkModeGate
      ? 'Chat recognized a multi-step job. Work mode can research, use tools and files, and keep going until it delivers a finished result. It is included with Plus.'
      : 'Plus unlocks Work mode for multi-step tasks and finished deliverables.'));

  const handleUpgrade = useCallback(async () => {
    if (isWorkModeGate && isPlus) {
      setIsStartingWork(true);
      try {
        await startWorkFromPrompt({
          prompt: originalMessage,
          fileIds: workFileIds,
          collectionId,
          agentId,
          router,
          source: 'work_plus_handoff',
        });
      } finally {
        setIsStartingWork(false);
      }
      return;
    }
    if (isModelGate) {
      openModelAccessPaywall(openModal, { title, subtitle });
      return;
    }
    openModal('PlusFeatureModal', { feature: isWorkModeGate ? 'work' : (buttonData?.feature || (agentId ? 'bots' : 'plus')) });
  }, [agentId, buttonData?.feature, collectionId, isModelGate, isPlus, isWorkModeGate, openModal, originalMessage, router, subtitle, title, workFileIds]);

  return (
    <View style={styles.container}>
      <View style={styles.card}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.iconContainer}>
            <Icon 
              name={isWorkModeGate ? 'briefcase' : 'sparkles'}
              size={20}
              color={PLUS_COLORS.iconColor}
              solid
            />
          </View>
          <View style={styles.headerText}>
            <Text size="n" weight="semibold" color="primary" style={styles.title}>
              {title}
            </Text>
            <Text size="s" color="secondary">
              {subtitle}
            </Text>
          </View>
        </View>

        {/* Original message preview */}
        {originalMessage && (
          <View style={styles.originalMessageContainer}>
            <View style={styles.originalMessageLabel}>
              <Icon 
                name="message" 
                size={12}
                color={colors.text_color_tertiary}
              />
              <Text size="xs" color="tertiary" weight="medium">
                Your message
              </Text>
            </View>
            <Text 
              size="n" 
              color="secondary" 
              style={styles.originalMessageText}
              numberOfLines={3}
            >
              {originalMessage}
            </Text>
          </View>
        )}

        {/* Footer with CTA */}
        <View style={styles.footer}>
          <View style={styles.benefitsList}>
            <View style={styles.benefitItem}>
              <Icon name="check" size={12} color={colors.text_color_tertiary} />
              <Text size="xs" color="tertiary">
                {isModelGate
                  ? 'Buy credits or upgrade to Plus'
                  : (isWorkModeGate ? 'Plans, researches, and uses tools' : 'Work mode for real, multi-step tasks')}
              </Text>
            </View>
            <View style={styles.benefitItem}>
              <Icon name="check" size={12} color={colors.text_color_tertiary} />
              <Text size="xs" color="tertiary">
                {isWorkModeGate ? 'Keeps working toward a finished result' : `${PLUS_MONTHLY_CREDITS_DISPLAY} monthly credits with Plus`}
              </Text>
            </View>
          </View>
          <Button
            variant="plus"
            text={isWorkModeGate && isPlus ? 'Run in Work' : (isModelGate ? 'Unlock' : 'Upgrade to Plus')}
            size="small"
            prependIcon={isWorkModeGate && isPlus ? 'briefcase' : 'sparkles'}
            onPress={handleUpgrade}
            isLoading={isStartingWork}
          />
        </View>
      </View>
    </View>
  );
}

PlusRequiredMessage.propTypes = {
  message: PropTypes.shape({
    content: PropTypes.string,
    buttons: PropTypes.array,
    errorType: PropTypes.string,
    parameters: PropTypes.object,
    files: PropTypes.array,
  }),
};

PlusRequiredMessage.displayName = 'PlusRequiredMessage';

export default React.memo(PlusRequiredMessage);
